// tenantClock.js — the one place the frontend turns "now" (or any
// timestamp) into a business date, or formats a timestamp for display.
// Everything uses the tenant's Timezone from Company Settings, never the
// viewer's device zone, so every user sees the same "today" and the date
// on a timestamp always matches the business date stamped from the same
// event. Mirrors backend/services/tenantClock.js.
//
// It is also the one place a date is shown or read back in the company
// Date Format (Company Settings > Localisation, vis_localisation.md):
// formatDate for any stored date or timestamp, formatDateTime for a
// timestamp with its time, formatDateWords for a month-name date, and
// parseDateText for what a user typed into a DateInput. Never format a
// date with toLocaleDateString('en-AU') or print a raw YYYY-MM-DD —
// ui-architecture.md "Dates".
//
// The zone and Date Format are module state: App.js sets them from Company
// Settings (and again after Company Settings is saved) before rendering
// any screen, and the server-rendered Pricelist PDF sets them from
// companyInfo (pricelistPdfEntry.js). Until then they fall back to
// Australia/Sydney and dd/mm/yyyy. Mirrors backend/utils/dates.js.
//
// DATEONLY arithmetic done in UTC — nextDayString (PricePricingPage.js),
// dayBefore/nextEffectiveFrom on the schedule pages — and the dashboard
// calendar's toISODate, which reads back a local Date built from y/m/d,
// don't need a zone and stay where they are.

const DEFAULT_TIME_ZONE = 'Australia/Sydney';

// The company Date Format choices — same list as backend/utils/dates.js.
export const DATE_FORMATS = [
  { value: 'dd/mm/yyyy', label: 'dd/mm/yyyy (26/12/2026)' },
  { value: 'mm/dd/yyyy', label: 'mm/dd/yyyy (12/26/2026)' },
  { value: 'yyyy-mm-dd', label: 'yyyy-mm-dd (2026-12-26)' },
];
const DEFAULT_DATE_FORMAT = 'dd/mm/yyyy';

// Earliest/latest date a user can enter — a 4-digit year either way.
export const MIN_DATE = '1000-01-01';
export const MAX_DATE = '9999-12-31';

let tenantTimeZone = DEFAULT_TIME_ZONE;
let tenantDateFormat = DEFAULT_DATE_FORMAT;

export function isValidTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-AU', { timeZone });
    return true;
  } catch {
    return false;
  }
}

export function setTenantTimeZone(timeZone) {
  tenantTimeZone = isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIME_ZONE;
}

export function getTenantTimeZone() {
  return tenantTimeZone;
}

export function setTenantDateFormat(format) {
  tenantDateFormat = DATE_FORMATS.some((f) => f.value === format) ? format : DEFAULT_DATE_FORMAT;
}

export function getTenantDateFormat() {
  return tenantDateFormat;
}

// 'YYYY-MM-DD' of `instant` on the tenant's wall calendar.
export function dateOnlyInTenant(instant = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tenantTimeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (type) => parts.find((p) => p.type === type).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function todayInTenant() {
  return dateOnlyInTenant(new Date());
}

// The tenant's today as a local-midnight Date — for calendar grids that
// step through days with a Date's own getDate()/setDate() (see
// dashboard/calendarConstants.js).
export function todayAsLocalDate() {
  const [y, m, d] = todayInTenant().split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Any Intl date/time format, pinned to the tenant zone.
export function formatInTenant(instant, options, locale = 'en-AU') {
  if (instant === null || instant === undefined || instant === '') return '';
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: tenantTimeZone }).format(date);
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// True only for a 'YYYY-MM-DD' string naming a real calendar date with a
// 4-digit year (2026-02-30 and 202222-12-26 both fail).
export function isValidDateOnly(value) {
  if (typeof value !== 'string') return false;
  const match = DATE_ONLY_RE.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < 1000) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

// 'YYYY-MM-DD' -> the company Date Format, e.g. '26/12/2026'.
function formatDateOnly(ymd) {
  const [, y, m, d] = DATE_ONLY_RE.exec(ymd);
  if (tenantDateFormat === 'mm/dd/yyyy') return `${m}/${d}/${y}`;
  if (tenantDateFormat === 'yyyy-mm-dd') return `${y}-${m}-${d}`;
  return `${d}/${m}/${y}`;
}

// Any date, shown in the company Date Format: a stored date
// ('YYYY-MM-DD', shown as-is, no zone involved) or a timestamp (the date
// it falls on in the tenant zone). Blank for nothing or an unparseable
// value.
export function formatDate(value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' && DATE_ONLY_RE.test(value)) return formatDateOnly(value);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatDateOnly(dateOnlyInTenant(date));
}

// '9:14 am'
export function formatTimeAU(instant) {
  return formatInTenant(instant, { hour: 'numeric', minute: '2-digit' });
}

// '25/09/2026 09:14 am' — the timeline/milestone date + time stamp. The
// date follows the company Date Format.
export function formatDateTime(instant) {
  if (!formatDate(instant)) return '';
  return `${formatDate(instant)} ${formatInTenant(instant, { hour: '2-digit', minute: '2-digit' })}`;
}

// The locale a month-name date is written in, so its day and month sit in
// the same order as the Date Format: 'Dec 26, 2026' for mm/dd/yyyy,
// '26 Dec 2026' otherwise.
export function dateWordsLocale() {
  return tenantDateFormat === 'mm/dd/yyyy' ? 'en-US' : 'en-AU';
}

// A month-name date ('26 Dec 2026', 'Friday 26 December 2026') — pass the
// Intl options for which parts to show. A stored 'YYYY-MM-DD' date is
// read as that calendar day; a timestamp shows in the tenant zone.
export function formatDateWords(value, options = { day: 'numeric', month: 'short', year: 'numeric' }) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' && DATE_ONLY_RE.test(value)) {
    const date = new Date(`${value}T00:00:00Z`);
    return new Intl.DateTimeFormat(dateWordsLocale(), { ...options, timeZone: 'UTC' }).format(date);
  }
  return formatInTenant(value, options, dateWordsLocale());
}

// A stored 'YYYY-MM-DD' date's month as a short name: 'Sept', 'Oct'.
export function formatMonthName(value) {
  if (typeof value !== 'string' || !DATE_ONLY_RE.test(value)) return '';
  return new Intl.DateTimeFormat(dateWordsLocale(), { month: 'short', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));
}

// A stored 'YYYY-MM-DD' date's weekday: 'Mon', or 'M' when narrow.
export function formatWeekdayShort(value, { narrow = false } = {}) {
  if (typeof value !== 'string' || !DATE_ONLY_RE.test(value)) return '';
  return new Intl.DateTimeFormat('en-AU', { weekday: narrow ? 'narrow' : 'short', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));
}

// What a user typed into a DateInput -> 'YYYY-MM-DD', or null when it isn't
// a real date with a 4-digit year. Reads the parts in the company Date
// Format's order; any of / - . or space separates them, and 8 digits with
// no separator work too (26122026). `strict` (used while typing) also
// needs a 2-digit day and month, so a half-edited '2/12/2026' isn't taken
// as 2 December.
export function parseDateText(text, { strict = false } = {}) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return null;
  let parts;
  if (/^\d{8}$/.test(trimmed)) {
    parts = tenantDateFormat === 'yyyy-mm-dd'
      ? [trimmed.slice(0, 4), trimmed.slice(4, 6), trimmed.slice(6)]
      : [trimmed.slice(0, 2), trimmed.slice(2, 4), trimmed.slice(4)];
  } else {
    parts = trimmed.split(/[/\-.\s]+/);
  }
  if (parts.length !== 3 || !parts.every((p) => /^\d+$/.test(p))) return null;
  let y; let m; let d;
  if (tenantDateFormat === 'yyyy-mm-dd') [y, m, d] = parts;
  else if (tenantDateFormat === 'mm/dd/yyyy') [m, d, y] = parts;
  else [d, m, y] = parts;
  if (y.length !== 4) return null;
  if (strict ? (d.length !== 2 || m.length !== 2) : (d.length > 2 || m.length > 2)) return null;
  const ymd = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  return isValidDateOnly(ymd) ? ymd : null;
}
